import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getCashFlowReport } from "./cashFlowService";
import { FaMoneyBillWave, FaPrint, FaArrowLeft, FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";
import "./CashFlow.css";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const CashFlowPage = () => {
  const navigate = useNavigate();
  const { canView, canExport } = usePermissions();

  if (!canView("cash_flow")) {
    return <AccessDenied moduleName="Cash Flow" action="view" />;
  }

  // ── Filters ───────────────────────────────────────────
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate]     = useState("");

  // ── Data ──────────────────────────────────────────────
  const [summary, setSummary]                   = useState(null);
  const [inflowBreakdown, setInflowBreakdown]   = useState([]);
  const [outflowBreakdown, setOutflowBreakdown] = useState([]);
  const [transactions, setTransactions]         = useState([]);
  const [searchQuery, setSearchQuery]           = useState("");
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);

  const hasFetched = useRef(false);

  // ── Fetch ──────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        start_date: startDate || undefined,
        end_date:   endDate   || undefined,
      };
      const data = await getCashFlowReport(filters);
      setSummary(data.summary || {});
      setInflowBreakdown(data.inflow_breakdown || []);
      setOutflowBreakdown(data.outflow_breakdown || []);
      setTransactions(data.transactions || []);
    } catch (err) {
      console.error("Cash Flow fetch error:", err);
      setError("Failed to load the Cash Flow report. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReport();
    }
  }, [fetchReport]);

  // ── Reset ──────────────────────────────────────────────
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setTimeout(() => fetchReport(), 0);
  };

  // ── Print ──────────────────────────────────────────────
  const handlePrint = () => window.print();

  // ── Filtered Transactions ──────────────────────────────
  const filteredTransactions = transactions.filter((tx) => {
    const q = searchQuery.toLowerCase().trim();
    return (
      (tx.voucher_no || "").toLowerCase().includes(q) ||
      (tx.party || "").toLowerCase().includes(q) ||
      (tx.description || "").toLowerCase().includes(q) ||
      (tx.category || "").toLowerCase().includes(q) ||
      (tx.cash_account || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="cf-page">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="cf-header">
        <h2>
          <FaMoneyBillWave className="cf-icon" /> Cash Flow Report
        </h2>
        <div className="cf-header-actions">
          <button className="cf-btn-print" onClick={handlePrint}>
            <FaPrint /> Print
          </button>
          <button
            className="cf-btn-back"
            onClick={() => navigate("/dashboard")}
          >
            ← Dashboard
          </button>
        </div>
      </div>

      {/* ── Summary Cards ───────────────────────────────── */}
      {summary && (
        <div className="cf-summary">
          <div className="cf-card card-opening">
            <div className="card-label">Opening Balance</div>
            <div className="card-value">{fmt(summary.opening_balance)}</div>
          </div>

          <div className="cf-card card-inflow">
            <div className="card-label">Total Inflows (+)</div>
            <div className="card-value">{fmt(summary.total_inflow)}</div>
          </div>

          <div className="cf-card card-outflow">
            <div className="card-label">Total Outflows (-)</div>
            <div className="card-value">{fmt(summary.total_outflow)}</div>
          </div>

          <div className={`cf-card ${summary.net_cash_flow >= 0 ? "card-net-positive" : "card-net-negative"}`}>
            <div className="card-label">Net Cash Flow</div>
            <div className="card-value">
              {summary.net_cash_flow >= 0 ? "+" : ""}
              {fmt(summary.net_cash_flow)}
            </div>
          </div>

          <div className="cf-card card-ending">
            <div className="card-label">Ending Balance</div>
            <div className="card-value">{fmt(summary.ending_balance)}</div>
          </div>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────── */}
      <div className="cf-filters">
        <div className="filter-group">
          <label>From Date</label>
          <input
            id="cf-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>To Date</label>
          <input
            id="cf-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button id="cf-apply-btn" className="cf-btn-filter" onClick={fetchReport}>
          Apply Filters
        </button>
        <button id="cf-reset-btn" className="cf-btn-reset" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* ── Content ─────────────────────────────────────── */}
      {loading ? (
        <div className="cf-loading">
          <div className="spinner" />
          Loading cash flow report…
        </div>
      ) : error ? (
        <div className="cf-loading text-danger">
          <FaExclamationTriangle className="me-2" /> {error}
        </div>
      ) : (
        <>
          {/* ── Side-by-side Breakdowns ──────────────────── */}
          <div className="cf-breakdown-grid">
            {/* Inflows column */}
            <div className="breakdown-column">
              <h3>Cash Inflows (Sources)</h3>
              {inflowBreakdown.length === 0 ? (
                <div className="breakdown-empty">No cash inflows in this period.</div>
              ) : (
                <div className="breakdown-list">
                  {inflowBreakdown.map((item, idx) => {
                    const pct = summary?.total_inflow ? (item.amount / summary.total_inflow) * 100 : 0;
                    return (
                      <div key={idx} className="breakdown-item">
                        <div className="breakdown-item-details">
                          <span className="breakdown-cat">{item.category}</span>
                          <span className="breakdown-amt amount-in">{fmt(item.amount)}</span>
                        </div>
                        <div className="breakdown-bar-bg">
                          <div className="breakdown-bar-fill fill-in" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="breakdown-pct">{pct.toFixed(1)}% of total inflows</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Outflows column */}
            <div className="breakdown-column">
              <h3>Cash Outflows (Uses)</h3>
              {outflowBreakdown.length === 0 ? (
                <div className="breakdown-empty">No cash outflows in this period.</div>
              ) : (
                <div className="breakdown-list">
                  {outflowBreakdown.map((item, idx) => {
                    const pct = summary?.total_outflow ? (item.amount / summary.total_outflow) * 100 : 0;
                    return (
                      <div key={idx} className="breakdown-item">
                        <div className="breakdown-item-details">
                          <span className="breakdown-cat">{item.category}</span>
                          <span className="breakdown-amt amount-out">{fmt(item.amount)}</span>
                        </div>
                        <div className="breakdown-bar-bg">
                          <div className="breakdown-bar-fill fill-out" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="breakdown-pct">{pct.toFixed(1)}% of total outflows</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Transaction Log Card ────────────────────── */}
          <div className="cf-table-card">
            <div className="cf-table-header">
              <div>
                <strong>Cash Flow Transaction Log</strong>
                <span> (All cash movements)</span>
              </div>
              <div className="cf-search-box">
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="cf-table-wrapper">
              <table className="cf-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}>#</th>
                    <th>Date</th>
                    <th>Voucher No</th>
                    <th>Type</th>
                    <th>Cash Account</th>
                    <th>Opposing Account</th>
                    <th>Party</th>
                    <th>Description</th>
                    <th className="center">Flow</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="center" style={{ padding: "40px", color: "#6b7280" }}>
                        No cash transactions found matching criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredTransactions.map((tx, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? "row-even" : "row-odd"}>
                        <td className="row-num">{idx + 1}</td>
                        <td>{tx.date || "N/A"}</td>
                        <td>
                          <span className="voucher-badge">{tx.voucher_no || "N/A"}</span>
                        </td>
                        <td>{tx.transaction_type}</td>
                        <td>{tx.cash_account}</td>
                        <td>{tx.opposing_account}</td>
                        <td>{tx.party || "-"}</td>
                        <td className="desc-cell" title={tx.description}>{tx.description || "-"}</td>
                        <td className="center">
                          <span className={`flow-badge ${tx.flow_type === "Inflow" ? "badge-in" : "badge-out"}`}>
                            {tx.flow_type}
                          </span>
                        </td>
                        <td className={`num ${tx.flow_type === "Inflow" ? "amount-in" : "amount-out"}`}>
                          {fmt(tx.amount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="cf-note">
              <span>💡</span>
              <div>
                <strong>About Cash Flow Categories:</strong> Inflows represent cash collections from customers or refunds received. Outflows represent expenditures such as supplier payments, salaries, customer refunds, and general operating expenses. Transfers within bank accounts are excluded as they net out.
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CashFlowPage;
